import { TimeRange } from '@hakimo-ui/hakimo/types';
import { TimeInputField } from '@hakimo-ui/hakimo/ui-elements';
import { Button, Modal } from '@hakimo-ui/shared/ui-base';
import { useState } from 'react';

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (timeRange: TimeRange | null) => void;
  initialTimeRange?: TimeRange;
}

export function TimeRangeSelectionModal(props: Props) {
  const { open, onClose, onSubmit, initialTimeRange } = props;

  const [timeRange, setTimeRange] = useState<{
    startTime: string;
    endTime: string;
  }>(initialTimeRange || { startTime: '', endTime: '' });

  const [showValidationError, setShowValidationError] = useState(false);

  const handleStartTimeChange = (value: string) => {
    setTimeRange({ ...timeRange, startTime: value });
    setShowValidationError(false);
  };

  const handleEndTimeChange = (value: string) => {
    setTimeRange({ ...timeRange, endTime: value });
    setShowValidationError(false);
  };

  const isValidTimeRange =
    (timeRange.startTime === '' && timeRange.endTime === '') ||
    (timeRange.startTime !== '' && timeRange.endTime !== '');

  const handleSubmit = () => {
    if (!isValidTimeRange) {
      setShowValidationError(true);
      return;
    }

    if (timeRange.startTime && timeRange.endTime) {
      onSubmit(timeRange);
    } else {
      onSubmit(null);
    }
    onClose();
  };

  const handleCancel = () => {
    setTimeRange(initialTimeRange || { startTime: '', endTime: '' });
    setShowValidationError(false);
    onClose();
  };

  const handleClear = () => {
    setTimeRange({ startTime: '', endTime: '' });
    setShowValidationError(false);
  };

  return (
    <Modal
      open={open}
      onClose={handleCancel}
      title="Add Time Range"
      footer={
        <>
          <Button variant="primary" onClick={handleSubmit}>
            Submit
          </Button>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
        </>
      }
    >
      <div className="mx-auto min-w-[30vw] rounded-lg p-6 shadow-lg">
        <div className="space-y-4">
          <div>
            <TimeInputField
              label="Start Time"
              value={timeRange.startTime}
              onChange={handleStartTimeChange}
            />
          </div>
          <div>
            <TimeInputField
              label="End Time"
              value={timeRange.endTime}
              onChange={handleEndTimeChange}
            />
          </div>
          {showValidationError && (
            <div className="text-alert-600 dark:text-alert-400 text-sm">
              Both start time and end time must be filled, or both must be
              empty.
            </div>
          )}
          {(timeRange.startTime || timeRange.endTime) && (
            <Button onClick={handleClear} variant="outline">
              Clear Time Range
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default TimeRangeSelectionModal;
